import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '@/api/client'
import type { OvertimeRuleRow } from '@/api/types'

/**
 * Overtime thresholds — what makes a week worth reviewing.
 *
 * These drive the "needs OT review" flag on the Problems screen and nothing
 * else. They do NOT calculate pay: the flag says someone should look at a
 * week, and a person decides what is owed. Worth stating plainly on the screen,
 * because a table of hour thresholds looks like a payroll engine and is not.
 *
 * The DEFAULT row (no state) is the federal 40-hour week and is what every
 * state without its own rule falls back to — most of the work. It is pinned to
 * the top and cannot be deleted, because losing it would leave the weekly
 * check with no threshold and the flag would go quiet everywhere.
 */

const BLANK = {
  state_code: '',
  daily_ot_hours: '',
  daily_dt_hours: '',
  weekly_ot_hours: '40',
  notes: '',
  is_active: true,
}

/** "" -> null, so a blank box means "no rule" rather than zero. */
function num(v: string): number | null {
  const t = v.trim()
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

function hrs(v: string | null): string {
  return v === null ? '—' : `${Number(v)} h`
}

export function OvertimeRulesPage() {
  const [rows, setRows] = useState<OvertimeRuleRow[] | null>(null)
  const [unruled, setUnruled] = useState<string[]>([])
  const [editing, setEditing] = useState<number | 'new' | null>(null)
  const [form, setForm] = useState({ ...BLANK })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [r, u] = await Promise.all([api.overtimeRules(), api.unruledStates()])
      setRows(r)
      setUnruled(u)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load overtime rules')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  function startEdit(r: OvertimeRuleRow) {
    setEditing(r.overtime_rule_id)
    setForm({
      state_code: r.state_code ?? '',
      daily_ot_hours: r.daily_ot_hours === null ? '' : String(Number(r.daily_ot_hours)),
      daily_dt_hours: r.daily_dt_hours === null ? '' : String(Number(r.daily_dt_hours)),
      weekly_ot_hours: r.weekly_ot_hours === null ? '' : String(Number(r.weekly_ot_hours)),
      notes: r.notes ?? '',
      is_active: r.is_active,
    })
    setNotice(null)
  }

  async function save() {
    setBusy(true)
    setError(null)
    try {
      const payload = {
        state_code: form.state_code.trim().toUpperCase() || null,
        daily_ot_hours: num(form.daily_ot_hours),
        daily_dt_hours: num(form.daily_dt_hours),
        weekly_ot_hours: num(form.weekly_ot_hours),
        notes: form.notes.trim() || null,
        is_active: form.is_active,
      }
      const next =
        editing === 'new'
          ? await api.addOvertimeRule(payload)
          : await api.updateOvertimeRule(editing as number, payload)
      setRows(next)
      setUnruled(await api.unruledStates())
      setEditing(null)
      setNotice('Saved. The Problems screen recalculates on its next load.')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save')
    } finally {
      setBusy(false)
    }
  }

  async function remove(r: OvertimeRuleRow) {
    if (
      !confirm(
        `Delete the ${r.state_code} rule? Jobs there fall back to the default ` +
          `week and daily overtime stops being flagged for that state.`,
      )
    )
      return
    setBusy(true)
    setError(null)
    try {
      setRows(await api.deleteOvertimeRule(r.overtime_rule_id))
      setUnruled(await api.unruledStates())
      setNotice(`${r.state_code} removed — those jobs now use the default rule.`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete')
    } finally {
      setBusy(false)
    }
  }

  const isDefault = (r: OvertimeRuleRow) => r.state_code === null

  return (
    <div className="stack">
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-info">{notice}</div>}

      <div className="card">
        <h2 className="title" style={{ marginTop: 0 }}>
          Overtime rules
        </h2>
        <p className="muted">
          These decide when a week is <strong>flagged for review</strong> on the
          Problems screen. They do not calculate anyone's pay — the flag says
          someone should look, and you decide what is owed.
        </p>
        <p className="muted" style={{ fontSize: '0.8125rem' }}>
          The field records every hour at straight time, so this flag is the only
          thing standing between a long week and a straight-time paycheque. A
          state with no rule of its own uses the default.
        </p>
      </div>

      {unruled.length > 0 && (
        <div className="alert alert-info">
          Open jobs with no rule of their own, using the default week:{' '}
          <strong>{unruled.join(', ')}</strong>. That is correct for states with
          no daily overtime law — add a rule only where one is owed.
        </div>
      )}

      {editing !== null && (
        <div className="card">
          <h2 className="title">
            {editing === 'new' ? 'Add a state rule' : 'Edit rule'}
          </h2>

          {editing === 'new' && (
            <div className="form-field">
              <label htmlFor="state">State</label>
              <select
                id="state"
                value={form.state_code}
                onChange={(e) => setForm({ ...form, state_code: e.target.value })}
              >
                <option value="">Pick a state…</option>
                {unruled.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <p className="muted" style={{ fontSize: '0.8125rem', margin: '0.35rem 0 0' }}>
                Only states with open jobs and no rule yet. A state's code is the
                identity of its rule and cannot be changed afterwards.
              </p>
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <div className="form-field" style={{ flex: 1, minWidth: 150 }}>
              <label htmlFor="dailyOt">Overtime over (hours/day)</label>
              <input
                id="dailyOt"
                type="number"
                step="0.5"
                min="1"
                max="24"
                value={form.daily_ot_hours}
                onChange={(e) => setForm({ ...form, daily_ot_hours: e.target.value })}
                placeholder="blank = no daily rule"
              />
            </div>
            <div className="form-field" style={{ flex: 1, minWidth: 150 }}>
              <label htmlFor="dailyDt">Double time over (hours/day)</label>
              <input
                id="dailyDt"
                type="number"
                step="0.5"
                min="1"
                max="24"
                value={form.daily_dt_hours}
                onChange={(e) => setForm({ ...form, daily_dt_hours: e.target.value })}
                placeholder="blank = none"
              />
            </div>
            <div className="form-field" style={{ flex: 1, minWidth: 150 }}>
              <label htmlFor="weeklyOt">Overtime over (hours/week)</label>
              <input
                id="weeklyOt"
                type="number"
                step="0.5"
                min="1"
                max="168"
                value={form.weekly_ot_hours}
                onChange={(e) => setForm({ ...form, weekly_ot_hours: e.target.value })}
                placeholder="40"
              />
            </div>
          </div>

          <div className="form-field">
            <label htmlFor="notes">Note</label>
            <input
              id="notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="What the rule is, and anything it does not model"
              maxLength={300}
            />
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            Active — untick to stop this rule flagging without deleting it
          </label>

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
            <button className="btn" disabled={busy} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button
              className="btn btn-secondary"
              disabled={busy}
              onClick={() => setEditing(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="crew-heading">
          <h2 className="title" style={{ margin: 0 }}>
            Rules
          </h2>
          {editing === null && unruled.length > 0 && (
            <button
              className="btn btn-secondary"
              style={{ marginLeft: 'auto' }}
              onClick={() => {
                setForm({ ...BLANK })
                setEditing('new')
              }}
            >
              Add a state
            </button>
          )}
        </div>

        {rows === null ? (
          <p className="muted">Loading…</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>State</th>
                  <th className="num">OT over (day)</th>
                  <th className="num">DT over (day)</th>
                  <th className="num">OT over (week)</th>
                  <th className="num">Open jobs</th>
                  <th>Note</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.overtime_rule_id} className={r.is_active ? '' : 'is-dismissed'}>
                    <td>
                      <strong className="mono">{r.state_code ?? 'Default'}</strong>
                      {isDefault(r) && (
                        <div className="muted" style={{ fontSize: '0.75rem' }}>
                          every state without its own rule
                        </div>
                      )}
                      {!r.is_active && (
                        <span className="badge badge-warn" style={{ marginLeft: 6 }}>
                          off
                        </span>
                      )}
                    </td>
                    <td className="num">{hrs(r.daily_ot_hours)}</td>
                    <td className="num">{hrs(r.daily_dt_hours)}</td>
                    <td className="num">{hrs(r.weekly_ot_hours)}</td>
                    <td className="num">
                      {isDefault(r) ? (
                        <span className="muted">—</span>
                      ) : (
                        r.job_count
                      )}
                    </td>
                    <td className="muted" style={{ fontSize: '0.8125rem' }}>
                      {r.notes}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button
                        className="btn btn-secondary"
                        style={{ minHeight: 32, fontSize: '0.8125rem' }}
                        onClick={() => startEdit(r)}
                      >
                        Edit
                      </button>
                      {/* The default has no delete: every state without its own
                          rule relies on it, so removing it would silence the
                          weekly check everywhere. */}
                      {!isDefault(r) && (
                        <button
                          className="btn btn-secondary"
                          style={{ minHeight: 32, fontSize: '0.8125rem', marginLeft: 6 }}
                          disabled={busy}
                          onClick={() => void remove(r)}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
